// @flow

import { completeRequest, beginRequest } from "@store/utils";
import type { StandardQuestionsState } from "./types";
import type { Action } from "redux-action";
import type { ApprovableContainer } from "@modules/service/types";
import type { StandardQuestion } from "@modules/service/reducer/standard-questions/types";

export type ShowStandardQuestionModalRequest = $Exact<{
  show: boolean,
  step?: number,
}>;

const startRequest = beginRequest(["requestsInProgress"]);

const finishRequest = completeRequest(["requestsInProgress"]);

export const onAddNewStandardQuestion = (state: StandardQuestionsState) => () =>
  startRequest(state);
export const onAddNewStandardQuestionSuccess = (state: StandardQuestionsState) => () =>
  finishRequest(state);
export const onAddNewStandardQuestionError = (state: StandardQuestionsState) => () =>
  finishRequest(state);

export const onUpdateStandardQuestion = (state: StandardQuestionsState) => () =>
  startRequest(state);
export const onUpdateStandardQuestionSuccess = (state: StandardQuestionsState) => () =>
  finishRequest(state);
export const onUpdateStandardQuestionError = (state: StandardQuestionsState) => () =>
  finishRequest(state);

export const onGetStandardQuestions = (state: StandardQuestionsState) => () => startRequest(state);

export const onGetStandardQuestionsSuccess = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  list: payload,
});

export const onGetStandardQuestionsError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onNewStandardQuestion = (state: StandardQuestionsState) => () => ({
  ...state,
  draft: {
    standardQuestion: {},
    advisorType: null,
    isActive: true,
  },
});

export const onShowAddStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Action<ShowStandardQuestionModalRequest>) => {
  if (!payload) {
    return state;
  }

  return {
    ...state,
    modals: {
      ...state.modals,
      showAddStandardQuestionModal: payload.show,
      modalStep: payload.step || state.modals.modalStep,
    },
  };
};

export const onUpdateStandardQuestionDraft = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draft: payload,
});

export const onSetStandardQuestionRevisionSchema = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  error: null,
  schema: payload,
});

export const onGetStandardQuestionActiveSuccess = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetStandardQuestionActiveError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onSetStandardQuestionAssignments = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  assignments: payload,
});

export const onSetStandardQuestionDraftAssignments = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  draftAssignments: payload,
});

export const onSetStandardQuestionStatus = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  configuration: {
    ...state.configuration,
    status: payload,
  },
});

export const onGetStandardQuestion = (state: StandardQuestionsState) => ({ payload }: Object) =>
  startRequest(state);

export const onGetStandardQuestionSuccess = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  error: null,
  current: payload,
});

export const onGetStandardQuestionError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowStandardQuestionEditModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditModal: payload,
  },
});

export const onSetShowSubmitStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showSubmitModal: payload,
  },
});

export const onSetShowCancelStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelModal: payload,
  },
});

export const onSubmitStandardQuestionForReviewError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelStandardQuestionReviewError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onStartStandardQuestionDraftSuccess = (state: StandardQuestionsState) => (
  action: Action<ApprovableContainer<StandardQuestion>>,
) => ({
  ...state,
  draft: (action.payload && action.payload.active) || {},
});

export const onSetShowDiscardStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDiscardModal: payload,
  },
});

export const onSetShowCancelDiscardStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showCancelDiscardModal: payload,
  },
});

export const onDuplicateStandardQuestionSuccess = (state: StandardQuestionsState) => (
  action: Action<StandardQuestion>,
) => ({
  ...state,
  draft: {
    standardQuestion: {},
    advisorType: (action.payload || {}).advisorType,
    isActive: (action.payload || {}).isActive,
  },
});

export const onSetShowDisableStandardQuestionModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showDisableModal: payload,
  },
});

export const onStartStandardQuestionDraftError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDiscardStandardQuestionDraftError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDuplicateStandardQuestionError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onCancelStandardQuestionReviewAndDiscardError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onDisableStandardQuestionError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onUpdateStandardQuestionAssignmentsError = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  ...finishRequest(state),
  success: false,
  error: payload,
});

export const onShowEditStandardQuestionAssignmentsModal = (state: StandardQuestionsState) => ({
  payload,
}: Object) => ({
  ...state,
  modals: {
    ...state.modals,
    showEditAssignmentsModal: payload,
  },
});
